import { useUserConfigurations } from "@via/api/configuration.tsx";
import { ActionButton } from "@via/components/action-button/action-button";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import { SkeletonLoader } from "@via/components/loading/loading";
import {
  useDetachOrganizationBoundary,
  useBindOrganizationRunEnvironmentBoundary,
  useGetOrganizationCloud,
  useMaterialiseOrganizationRunEnvironment,
  useUnbindOrganizationRunEnvironmentBoundary,
} from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { type OrganizationTab } from "@via/pages/account/admin/organizations/organization-modal.tsx";
import AttachBoundaryModal from "@via/pages/account/admin/organizations/panels/attach-boundary-modal.tsx";
import RoleArnsPanel from "@via/pages/account/admin/organizations/panels/role-arns-panel.tsx";
import ServiceAccountsPanel from "@via/pages/account/admin/organizations/panels/service-accounts-panel.tsx";
import {
  BoundaryKind,
  GetUserOrganizationDetailsForAdminResponseBodyItem,
  OrganizationCloudAttachment,
  OrganizationCloudState,
  PROVISIONABLE_BOUNDARY_KINDS,
} from "common";
import { type JSX, useState } from "react";

const REFUSING_STATES: ReadonlySet<OrganizationCloudState> =
  new Set<OrganizationCloudState>([
    "corrupt",
    "invalidBinding",
    "ambiguous",
    "blocked",
  ]);

/**
 * The headline is presentation; the cause arrives from the server as blockingReason and is
 * rendered verbatim underneath, so this map never restates a rule the resolver owns.
 */
const STATE_HEADLINES: Record<OrganizationCloudState, string> = {
  corrupt: "A launch will refuse: this organization's cloud records conflict.",
  notApplicable: "No cloud run environment applies to this organization.",
  noRunEnvironment: "No cloud run environment exists yet.",
  invalidBinding:
    "A launch will refuse: this run environment's binding is no longer usable.",
  bound: "Bound to an attached boundary. A launch will use it.",
  willBindOnLaunch: "The attached boundary will be bound at first launch.",
  willClaimFromPool:
    "A free boundary will be claimed from the pool at first launch.",
  ambiguous:
    "A launch will refuse: several boundaries are attached and none is bound.",
  blocked: "A launch will refuse: no boundary is attached.",
};

function VendorSection({
  title,
  kind,
  attachments,
  bannerTestId,
  children,
}: {
  title: string;
  kind: BoundaryKind;
  attachments: OrganizationCloudAttachment[];
  bannerTestId: string;
  children: JSX.Element;
}): JSX.Element {
  const attached = attachments.some(a => a.cloudVendor === kind);
  return (
    <section>
      <h3 className="via-h3 pb-2">{title}</h3>
      {!attached && (
        <div
          data-testid={bannerTestId}
          className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Entries here will not reach a run until a {kind} boundary is attached
          to this organization.
        </div>
      )}
      {children}
    </section>
  );
}

/** The states in which an attachment row offers Bind (spec 6.1: states 4, 6 and 8). */
const BIND_OFFERING_STATES: ReadonlySet<OrganizationCloudState> =
  new Set<OrganizationCloudState>([
    "invalidBinding",
    "willBindOnLaunch",
    "ambiguous",
  ]);

/**
 * Renders what the server resolved, never re-deriving it: the launch decision lives in one place
 * and this panel reports it. Every write action honours `writesDisabled`: while the records are
 * corrupt nothing on this tab may offer an action.
 */
export default function CloudPanel({
  organization,
  onNavigateToTab,
}: {
  organization: GetUserOrganizationDetailsForAdminResponseBodyItem;
  /**
   * Switches the modal to another tab. Present so this tab can point at the Run Environments tab
   * without growing a second listing of its own: one collection, one representation (decision 8).
   */
  onNavigateToTab?: (tab: OrganizationTab) => void;
}): JSX.Element {
  const organizationId = organization?.id ?? "";
  const { data: cloud, isLoading } = useGetOrganizationCloud(
    { organizationId },
    !!organizationId
  );
  const { data: configurations } = useUserConfigurations();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [detachTarget, setDetachTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [unbindTarget, setUnbindTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const detachMutation = useDetachOrganizationBoundary();
  const bindMutation = useBindOrganizationRunEnvironmentBoundary();
  const createMutation = useMaterialiseOrganizationRunEnvironment();
  const unbindMutation = useUnbindOrganizationRunEnvironmentBoundary();

  if (isLoading || !cloud) {
    return (
      <div className="p-4">
        <SkeletonLoader count={2} />
      </div>
    );
  }

  const refusing = REFUSING_STATES.has(cloud.effectiveState);
  const writesEnabled = !cloud.writesDisabled;
  // Every Cloud write is disabled while the records conflict, and the vendor allowlists' Add and
  // Remove are Cloud writes, so their sections leave the screen entirely rather than offering
  // controls that must not act.
  const permittedVendorKinds = cloud.writesDisabled
    ? []
    : PROVISIONABLE_BOUNDARY_KINDS.filter(kind =>
        (configurations?.BOUNDARY_KINDS ?? []).includes(kind)
      );
  const boundAttachment = cloud.runEnvironment?.boundAttachmentId
    ? cloud.attachments.find(
        a =>
          a.organizationCustomerBoundaryId ===
          cloud.runEnvironment?.boundAttachmentId
      )
    : undefined;

  return (
    <div className="space-y-8">
      <div
        data-testid="cloud-state-banner"
        className={
          refusing
            ? "rounded-md border border-red-300 bg-red-50 p-4"
            : "rounded-md border border-gray-200 bg-gray-50 p-4"
        }>
        <div
          className={
            refusing
              ? "text-sm font-medium text-red-800"
              : "text-sm font-medium text-gray-900"
          }>
          {STATE_HEADLINES[cloud.effectiveState]}
        </div>
        {cloud.blockingReason && (
          <div
            className={
              refusing
                ? "mt-1 text-sm text-red-700"
                : "mt-1 text-sm text-gray-600"
            }>
            {cloud.blockingReason}
          </div>
        )}
        {writesEnabled && cloud.effectiveState === "noRunEnvironment" && (
          <div className="mt-3">
            <ActionButton
              button="Create run environment"
              showIcon={false}
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate({ organizationId }, {})}
            />
          </div>
        )}
      </div>

      {cloud.duplicateSystemGeneratedRunEnvironments.length > 0 && (
        <div
          data-testid="cloud-duplicate-environments"
          className="rounded-md border border-red-300 p-4 text-sm">
          <div className="font-medium text-red-800">
            Conflicting system generated run environments
          </div>
          <ul className="mt-2 list-disc pl-5 text-gray-900">
            {cloud.duplicateSystemGeneratedRunEnvironments.map(environment => (
              <li key={environment.id}>
                #{environment.id} {environment.name}
                {environment.boundAttachmentId ? " (bound)" : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <section>
        <div className="flex items-center justify-between pb-2">
          <h3 className="via-h3">Attached boundaries</h3>
          {writesEnabled && (
            <ActionButton
              button="Attach"
              showIcon={false}
              onClick={() => setPickerOpen(true)}
            />
          )}
        </div>
        {cloud.attachments.length === 0 ? (
          <div className="text-sm text-gray-600">
            No boundary is attached to this organization.
          </div>
        ) : (
          <ul
            data-testid="cloud-attachments"
            className="divide-y divide-gray-200 rounded-md border border-gray-200">
            {cloud.attachments.map(attachment => (
              <li
                key={attachment.organizationCustomerBoundaryId}
                className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1 truncate font-medium text-gray-900">
                    {attachment.label}
                  </div>
                  <div className="shrink-0 text-gray-600">
                    {attachment.cloudVendor}
                  </div>
                </div>
                <div className="mt-1 text-gray-600">
                  Attached{" "}
                  {new Date(attachment.attachedAt).toLocaleDateString()}
                  {attachment.boundToRunEnvironmentIds.length > 0 && (
                    <>
                      {" "}
                      · bound by run environment
                      {attachment.boundToRunEnvironmentIds.length > 1
                        ? "s"
                        : ""}{" "}
                      {attachment.boundToRunEnvironmentIds.join(", ")}
                    </>
                  )}
                </div>
                {!attachment.canDetach && attachment.detachBlockedReason && (
                  <div className="mt-1 text-amber-700">
                    {attachment.detachBlockedReason}
                  </div>
                )}
                {writesEnabled && (
                  <div className="mt-2 flex gap-2">
                    {cloud.runEnvironment &&
                      BIND_OFFERING_STATES.has(cloud.effectiveState) && (
                        <ActionButton
                          button="Bind"
                          id={`bind-${attachment.organizationCustomerBoundaryId}`}
                          showIcon={false}
                          primary={false}
                          disabled={bindMutation.isPending}
                          onClick={() =>
                            bindMutation.mutate(
                              {
                                organizationId,
                                profileClusterId: cloud.runEnvironment!.id,
                                organizationCustomerBoundaryId:
                                  attachment.organizationCustomerBoundaryId,
                              },
                              {}
                            )
                          }
                        />
                      )}
                    <ActionButton
                      button="Detach"
                      id={`detach-${attachment.organizationCustomerBoundaryId}`}
                      showIcon={false}
                      primary={false}
                      disabled={!attachment.canDetach}
                      onClick={() =>
                        setDetachTarget({
                          id: attachment.organizationCustomerBoundaryId,
                          name: attachment.label,
                        })
                      }
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {cloud.runEnvironment && (
        <section data-testid="cloud-run-environment">
          <h3 className="via-h3 pb-2">Run environment</h3>
          <div className="rounded-md border border-gray-200 p-4 text-sm">
            <div className="font-medium text-gray-900">
              {cloud.runEnvironment.name}{" "}
              <span className="text-gray-500">#{cloud.runEnvironment.id}</span>
            </div>
            <div className="mt-1 text-gray-600">
              {cloud.runEnvironment.boundAttachmentId
                ? `Bound to ${boundAttachment?.label ?? cloud.runEnvironment.boundAttachmentId}.`
                : "Not bound to a boundary yet."}
            </div>
            {onNavigateToTab && (
              <button
                type="button"
                onClick={() => onNavigateToTab("Run Environments")}
                className="mt-1 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                Manage this organization&apos;s run environments
              </button>
            )}
            {writesEnabled && cloud.runEnvironment.boundAttachmentId && (
              <div className="mt-2">
                <ActionButton
                  button="Unbind"
                  showIcon={false}
                  primary={false}
                  disabled={unbindMutation.isPending}
                  onClick={() =>
                    setUnbindTarget({
                      id: cloud.runEnvironment!.id,
                      name:
                        boundAttachment?.label ??
                        cloud.runEnvironment!.boundAttachmentId ??
                        "",
                    })
                  }
                />
              </div>
            )}
          </div>
        </section>
      )}

      {pickerOpen && (
        <AttachBoundaryModal
          organizationId={organizationId}
          permittedKinds={permittedVendorKinds}
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <DeleteModal
        item={detachTarget}
        setItem={setDetachTarget}
        isLoading={detachMutation.isPending}
        title="Detach customer boundary"
        confirmText="Detach boundary"
        message={name => (
          <>
            Revoke the attachment of{" "}
            <strong className="font-semibold">{name}</strong>? The boundary
            itself is kept and returns to the unclaimed pool.
          </>
        )}
        deleteFunction={() =>
          new Promise<boolean>(resolve => {
            detachMutation.mutate(
              {
                organizationId,
                organizationCustomerBoundaryId: detachTarget!.id,
              },
              {
                onSuccess: () => resolve(true),
                onError: () => resolve(false),
              }
            );
          })
        }
      />

      <DeleteModal
        item={unbindTarget}
        setItem={setUnbindTarget}
        isLoading={unbindMutation.isPending}
        title="Remove boundary binding"
        confirmText="Unbind binding"
        message={name => (
          <>
            Remove this run environment&apos;s binding to{" "}
            <strong className="font-semibold">{name}</strong>? The attachment
            stays available for a later bind.
          </>
        )}
        deleteFunction={() =>
          new Promise<boolean>(resolve => {
            unbindMutation.mutate(
              { organizationId, profileClusterId: unbindTarget!.id },
              {
                onSuccess: () => resolve(true),
                onError: () => resolve(false),
              }
            );
          })
        }
      />

      {permittedVendorKinds.includes(BoundaryKind.GCP) && (
        <VendorSection
          title="Google Cloud"
          kind={BoundaryKind.GCP}
          attachments={cloud.attachments}
          bannerTestId="gcp-unattached-banner">
          <ServiceAccountsPanel organization={organization} />
        </VendorSection>
      )}
      {permittedVendorKinds.includes(BoundaryKind.AWS) && (
        <VendorSection
          title="AWS"
          kind={BoundaryKind.AWS}
          attachments={cloud.attachments}
          bannerTestId="aws-unattached-banner">
          <RoleArnsPanel organization={organization} />
        </VendorSection>
      )}
    </div>
  );
}
